import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiPlay, FiCheck, FiClock, FiStar, FiPhone, FiMail, FiUser, FiBriefcase, FiChevronDown, FiSearch, FiFilter } from 'react-icons/fi';
import './App.css';
import HeroSection from './components/HeroSection';
import WorkshopTimer from './components/WorkshopTimer';
import { useWorkshopSchedule } from './hooks/useWorkshopSchedule';

const WEBHOOK_URL = "https://connect.pabbly.com/workflow/sendwebhookdata/IjU3NjYwNTY0MDYzMjA0MzE1MjY0NTUzNDUxMzAi_pc";

// Video testimonials data - Kept structure, can be updated with new video IDs later
const testimonialVideos = [
  {
    id: 1,
    url: "https://www.youtube.com/watch?v=v3EEtDIaC3w",
    embedId: "v3EEtDIaC3w",
    thumbnail: "https://img.youtube.com/vi/v3EEtDIaC3w/maxresdefault.jpg",
    title: "How I Automated My Sales Process",
    name: "Business Coach"
  },
  {
    id: 2,
    url: "https://youtu.be/IrfPwkcSTB0?si=4zR7J5O2aIbdr7k0",
    embedId: "IrfPwkcSTB0",
    thumbnail: "https://img.youtube.com/vi/IrfPwkcSTB0/maxresdefault.jpg",
    title: "Generating Leads on Autopilot",
    name: "Marketing Consultant"
  },
  {
    id: 3,
    url: "https://youtu.be/F26JiMS-w4I?si=5CoJ4w0AnUAMPYZm",
    embedId: "F26JiMS-w4I",
    thumbnail: "https://img.youtube.com/vi/F26JiMS-w4I/maxresdefault.jpg",
    title: "No More Manual Follow-ups",
    name: "Agency Owner"
  },
  {
    id: 4,
    url: "https://youtu.be/yWzJt7jv5CM?si=cFmZMEoNQgvFxmh4",
    embedId: "yWzJt7jv5CM",
    thumbnail: "https://img.youtube.com/vi/yWzJt7jv5CM/maxresdefault.jpg",
    title: "Making Sales While I Sleep",
    name: "Course Creator"
  },
  {
    id: 5,
    url: "https://youtu.be/fUCYE7BoTJo?si=vFUxnRuWJZE2zRKI",
    embedId: "fUCYE7BoTJo",
    thumbnail: "https://img.youtube.com/vi/fUCYE7BoTJo/maxresdefault.jpg",
    title: "WhatsApp Automation Changed Everything",
    name: "Trading Mentor"
  },
  {
    id: 6,
    url: "https://youtu.be/vRzrNK9XPmQ?si=LGC2-FHUQ0SlctTo",
    embedId: "vRzrNK9XPmQ",
    thumbnail: "https://img.youtube.com/vi/vRzrNK9XPmQ/maxresdefault.jpg",
    title: "Consistent Leads Without Ads",
    name: "Healthcare Professional"
  }
];

// Client Feedback data
const clientFeedbacks = [
  {
    id: 1,
    name: "A R Ahamed Junaith",
    role: "Spoken English Coach",
    image: "https://storage.files-vault.com/uploads/1760941910-ht57JBdJ4f.webp",
    quote: "Automated my entire lead follow-up system. Revenue increased by 3X in just 2 months using WhatsApp funnels."
  },
  {
    id: 2,
    name: "Maria Sundar",
    role: "Stock Trader Coach",
    image: "https://storage.files-vault.com/uploads/1760942061-nsFEgCbsBd.webp",
    quote: "I used to spend hours on calls. Now AI handles the initial conversation and I only talk to qualified leads."
  },
  {
    id: 3,
    name: "Arjumand",
    role: "Community Growth Expert",
    image: "https://storage.files-vault.com/uploads/1760942064-I6HBtoRq5t.webp",
    quote: "Zero ad spend, just organic WhatsApp automation. Got 60 registrations in a week effortlessly."
  },
  {
    id: 4,
    name: "Subiya Fathima",
    role: "Bridal Leads Coach",
    image: "https://storage.files-vault.com/uploads/1760942062-cWbhjZc7n9.webp",
    quote: "Set up the system once, and it keeps bringing sales. It's truly 'Sales on Autopilot' as promised."
  },
  {
    id: 5,
    name: "Manigandan",
    role: "FiveStar",
    image: "https://storage.files-vault.com/uploads/1760942066-tiqyCho8Gf.webp",
    quote: "The 'While You Sleep' concept is real. Woke up to 5 new high-ticket sales notifications today."
  },
  {
    id: 6,
    name: "Bindhu Madhavan",
    role: "Prompt Committee",
    image: null,
    quote: "From tech-overwhelmed to tech-enabled. The free tools and strategies taught here are game changers."
  }
];

// FAQ data - Updated for Free Daily Sales Automation Webinar
const faqData = [
  {
    id: 1,
    question: "When is this Free Sales Automation Webinar happening?",
    answer: "This is a 3-hour FREE LIVE online webinar happening DAILY from 4:00 PM to 7:00 PM IST. Once you register, you'll get the joining link by email and WhatsApp for the next available session."
  },
  {
    id: 2,
    question: "Is this webinar really free?",
    answer: "Yes, it's 100% FREE! No hidden costs, no surprise charges. Abdul Hakkim A teaches daily to help entrepreneurs break free from the manual grind and start automating their sales."
  },
  {
    id: 3,
    question: "Is this session live or pre-recorded?",
    answer: "It's 100% LIVE — Abdul Hakkim A himself teaches daily, explains the WhatsApp & AI systems step-by-step, and answers your questions in real time."
  },
  {
    id: 4,
    question: "Do I need technical coding knowledge?",
    answer: "No. This webinar is designed for non-techies. We use FREE WhatsApp tools and simple AI setups that require zero coding."
  },
  {
    id: 5,
    question: "Will I get recordings if I can't attend live?",
    answer: "No Recording will be provided. It's a Live only Webinar to ensure you take action immediately. Since we run daily sessions, you can join the next day if you miss one."
  },
  {
    id: 6,
    question: "I have a small business, will this work for me?",
    answer: "Yes! Whether you are a coach, consultant, freelancer, or run a small agency, these automation strategies work to bring in leads on autopilot."
  },
  {
    id: 7,
    question: "What happens after I register?",
    answer: "You'll immediately receive a confirmation email and WhatsApp message with your unique joining link for the next available session, your bonus details, and instructions."
  },
  {
    id: 8,
    question: "Is there support if I get stuck?",
    answer: "You'll get Lifetime Access to the community where you can ask questions and get help setting up your automation."
  },
  {
    id: 9,
    question: "Can I attend multiple sessions?",
    answer: "Yes! Since we run daily sessions, you can attend multiple times to reinforce your learning and ask different questions each time."
  },
  {
    id: 10,
    question: "What's the catch? Why is it free?",
    answer: "No catch! Our mission is to help entrepreneurs succeed. We believe in providing value first. There may be optional paid programs mentioned, but the webinar content is completely free."
  },
  {
    id: 11,
    question: "Still have a doubt?",
    answer: "Just WhatsApp us anytime — our team is ready to help you get started with confidence. Email: support@yinguru.com"
  }
];

// Country codes for phone input
const countryCodes = [
  { code: "+91", country: "IN", flag: "🇮🇳" },
  { code: "+1", country: "US", flag: "🇺🇸" },
  { code: "+44", country: "UK", flag: "🇬🇧" },
  { code: "+65", country: "SG", flag: "🇸🇬" },
  { code: "+971", country: "AE", flag: "🇦🇪" }
];

function App() {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+91',
    phone: '',
    profession: '',
    consent: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [faqSearch, setFaqSearch] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('all');
  const [expandedFeedback, setExpandedFeedback] = useState({});
  const [hasAutoPopupShown, setHasAutoPopupShown] = useState(false);

  const modalRef = useRef(null);
  const firstInputRef = useRef(null);
  const autoPopupTimeoutRef = useRef(null);

  // Use workshop schedule hook
  const { workshopDetails, countdownTarget } = useWorkshopSchedule();

  // Auto-popup registration modal after 10 seconds
  useEffect(() => {
    // Only show auto-popup once per session and if modal isn't already open
    if (!hasAutoPopupShown && !isFormModalOpen) {
      autoPopupTimeoutRef.current = setTimeout(() => {
        setIsFormModalOpen(true);
        setHasAutoPopupShown(true);
        // Track auto-popup event with Facebook Pixel
        if (typeof fbq !== 'undefined') {
          fbq('track', 'ViewContent', {
            content_name: 'Free Sales Automation Webinar - Auto Popup',
            content_category: 'Webinar',
            content_type: 'auto_popup',
            value: 0,
            currency: 'INR'
          });
        }
      }, 10000); // 10 seconds
    }

    // Cleanup function to prevent memory leaks
    return () => {
      if (autoPopupTimeoutRef.current) {
        clearTimeout(autoPopupTimeoutRef.current);
        autoPopupTimeoutRef.current = null;
      }
    };
  }, [hasAutoPopupShown, isFormModalOpen]);

  // Modal focus management and keyboard handling
  useEffect(() => {
    if (isFormModalOpen && firstInputRef.current) {
      firstInputRef.current.focus();
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsFormModalOpen(false);
        setSelectedVideo(null);
      }
    };

    if (isFormModalOpen || selectedVideo) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isFormModalOpen, selectedVideo]);

  // Debounced FAQ search
  const handleFaqSearch = useCallback((value) => {
    setFaqSearch(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    const newTimeout = setTimeout(() => {}, 300);
    setSearchTimeout(newTimeout);
  }, [searchTimeout]);

  const filteredFaqs = useMemo(() => {
    if (!faqSearch.trim()) return faqData;
    return faqData.filter(faq =>
      faq.question.toLowerCase().includes(faqSearch.toLowerCase()) ||
      faq.answer.toLowerCase().includes(faqSearch.toLowerCase())
    );
  }, [faqSearch]);

  const filteredFeedbacks = useMemo(() => {
    if (selectedRoleFilter === 'all') return clientFeedbacks;
    return clientFeedbacks.filter(feedback =>
      feedback.role.toLowerCase().includes(selectedRoleFilter.toLowerCase())
    );
  }, [selectedRoleFilter]);

  const uniqueRoles = useMemo(() => 
    ['all', ...new Set(clientFeedbacks.map(f => f.role))]
  , []);

  const toggleFaq = (faqId) => setExpandedFaq(expandedFaq === faqId ? null : faqId);
  const toggleFeedbackExpanded = (feedbackId) => setExpandedFeedback(prev => ({ ...prev, [feedbackId]: !prev[feedbackId] }));

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Please enter a valid email';
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    else if (!/^\d{10}$/.test(formData.phone.replace(/\s/g, ''))) errors.phone = 'Please enter a valid 10-digit phone number';
    if (!formData.profession.trim()) errors.profession = 'Profession/Role is required';
    if (!formData.consent) errors.consent = 'Please agree to the terms';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const phoneWithCountryCode = `${formData.countryCode}${formData.phone}`;

      // Get tracking data from URL parameters
      const trackingData = window.trackingData || {};

      // Prepare webhook payload
      const webhookPayload = {
        name: formData.name,
        email: formData.email,
        phone: phoneWithCountryCode,
        profession: formData.profession,
        timestamp: new Date().toISOString(),
        source: 'landing_page',
        webinar_date: workshopDetails.formattedDate,
        webinar_time: workshopDetails.timeString,
        webinar_type: 'free_daily_session',
        registration_type: 'free',
        ...trackingData // Include all tracking parameters
      };

      // Send to webhook
      try {
        await fetch(WEBHOOK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(webhookPayload)
        });
      } catch (webhookError) {
        console.warn('Webhook submission failed:', webhookError);
      }

      // Track Facebook Pixel conversion
      if (typeof fbq !== 'undefined') {
        fbq('track', 'Lead', {
          content_name: 'Free Sales Automation Webinar Registration - Daily Session',
          content_category: 'Webinar',
          value: 0,
          currency: 'INR'
        });
      }

      setShowSuccess(true);
      setIsFormModalOpen(false);
      setFormData({
        name: '',
        email: '',
        countryCode: '+91',
        phone: '',
        profession: '',
        consent: false
      });

      setTimeout(() => setShowSuccess(false), 5000);

    } catch (error) {
      console.error('Form submission error:', error);
      alert('There was an error submitting the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: '' }));
  };

  const getInitials = (name) => name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);

  // Handle manual modal open (clear auto-popup timer if it exists)
  const handleManualModalOpen = () => {
    if (autoPopupTimeoutRef.current) {
      clearTimeout(autoPopupTimeoutRef.current);
      autoPopupTimeoutRef.current = null;
    }
    setHasAutoPopupShown(true); // Prevent auto-popup from showing later
    setIsFormModalOpen(true);
  };

  return (
    <div className="app">
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            className="success-toast"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
          >
            <FiCheck className="success-icon" />
            Registration successful! Check your email for webinar link.
          </motion.div>
        )}
      </AnimatePresence>

      <HeroSection openRegistration={handleManualModalOpen} />

      {/* Section 2: Problem & Agitation - UPDATED CONTENT */}
      <motion.section
        className="problem-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <motion.div className="section-content">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              😰 Is This What Your Business Feels Like Right Now?
            </motion.h2>

            <motion.div
              className="problem-intro"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <p>You have big dreams for your business, but the daily reality is a struggle. You are working hard, but the results just aren't showing.</p>
            </motion.div>

            <div className="problem-grid">
              {[
                {
                  emoji: "🚀",
                  title: "Don't Know Where to Start?",
                  desc: "Want to start an online business and earn extra income… but don't know the first step? The information overload is paralyzing."
                },
                {
                  emoji: "📉",
                  title: "Hard to Get Clients?",
                  desc: "Finding it hard to get and keep clients consistently? You spend days chasing leads, but conversions are low and unpredictable."
                },
                {
                  emoji: "😫",
                  title: "Stressed & Burnt Out?",
                  desc: "Working too much and feeling stressed, burnt out, and overwhelmed? You became an entrepreneur for freedom, but you're working 24/7."
                },
                {
                  emoji: "💸",
                  title: "Money Stress?",
                  desc: "Dealing with irregular income and money stress every month? The 'feast or famine' cycle is keeping you up at night."
                },
                {
                  emoji: "🤯",
                  title: "Tech Overwhelm?",
                  desc: "Struggling to keep up with new skills and technology in a fast-changing world? AI, Funnels, Automation... it all feels too complex."
                },
                {
                  emoji: "👻",
                  title: "Feeling Invisible?",
                  desc: "So much competition that you feel invisible or stuck? Your competitors seem to be growing while you are standing still."
                },
                {
                  emoji: "💔",
                  title: "Client Engagement?",
                  desc: "Having trouble keeping your clients engaged, motivated, or getting results? Manual follow-ups are draining your energy."
                }
              ].map((problem, index) => (
                <motion.div
                  key={index}
                  className="problem-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                >
                  <h3>{problem.emoji} {problem.title}</h3>
                  <p>{problem.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Section 3: The Solution - UPDATED for Sales Automation */}
      <motion.section
        className="solution-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <motion.div className="section-content">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              🎯 The Solution: Unlimited Quality Leads & Sales On Autopilot
            </motion.h2>

            <motion.div
              className="solution-intro"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <p>Imagine waking up to new sales notifications every morning. This isn't magic—it's <strong>Sales Automation</strong>. We teach you how to use <strong>FREE WhatsApp & AI Funnels</strong> to generate revenue even while you sleep.</p>
            </motion.div>

            <div className="comparison-table">
              <motion.div
                className="old-way"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h3>❌ The Old Struggle</h3>
                <ul>
                  <li>Manually replying to every DM</li>
                  <li>Chasing leads who ghost you</li>
                  <li>Inconsistent monthly income</li>
                  <li>Confused by paid ads & complex tech</li>
                  <li>Working weekends to catch up</li>
                  <li>Feeling invisible in the market</li>
                </ul>
              </motion.div>

              <motion.div
                className="new-way"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <h3>✅ The Automation Way</h3>
                <ul>
                  <li><strong>Unlimited Quality Leads</strong> on Autopilot</li>
                  <li>Sales happening <strong>While You Sleep</strong></li>
                  <li>Using <strong>FREE WhatsApp Tools</strong></li>
                  <li>AI handles the follow-ups for you</li>
                  <li>Predictable revenue & growth</li>
                  <li>More time for yourself & family</li>
                </ul>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Section 4: What You Will Discover - UPDATED */}
      <motion.section
        className="discovery-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <motion.div className="section-content">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              🚀 What You'll Discover in This FREE Sales Automation Webinar
            </motion.h2>

            <motion.div
              className="discovery-intro"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <p>Stop trading time for money. Learn the exact system to automate your business growth - completely FREE.</p>
            </motion.div>

            <div className="discovery-grid">
              {[
                {
                  icon: "🤖",
                  title: "AI & WhatsApp Funnels",
                  desc: "How to set up your own sales funnel using FREE WhatsApp tools and AI—no coding required."
                },
                {
                  icon: "😴",
                  title: "Sales While You Sleep",
                  desc: "The secret framework to making your business run 24/7, generating sales even when you're resting."
                },
                {
                  icon: "📈",
                  title: "Unlimited Quality Leads",
                  desc: "Strategies to attract high-paying clients who are ready to buy, without chasing them manually."
                },
                {
                  icon: "💰",
                  title: "Zero Cost Automation",
                  desc: "Why you don't need expensive software. We show you how to do it with tools you already have."
                },
                {
                  icon: "⚡",
                  title: "Instant Implementation",
                  desc: "Actionable steps you can apply immediately during the webinar to see results."
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  className="discovery-item"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -8, scale: 1.02 }}
                >
                  <div className="discovery-icon">{item.icon}</div>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Section 5: Meet Your Mentor - UPDATED for Abdul Hakkim A */}
      <section className="mentor-section py-16 bg-[#0b1324] text-gray-200">
        <div className="container mx-auto px-4">
          <motion.div
            className="mentor-content grid items-center gap-10 lg:grid-cols-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="flex justify-center lg:justify-end">
              <motion.div
                className="rounded-3xl border-2 border-orange-400 p-3 shadow-lg shadow-orange-500/20 bg-[#101c33]"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src="https://storage.files-vault.com/uploads/1761032692-VSdwsp8AG0.png"
                  alt="Abdul Hakkim A - Sales Automation Expert"
                  loading="lazy"
                  className="rounded-2xl w-full max-w-[360px] md:max-w-[400px] object-cover"
                />
              </motion.div>
            </div>

            <div className="mentor-info max-w-xl mx-auto lg:mx-0">
              <motion.h2
                className="text-3xl md:text-4xl font-bold text-orange-400 mb-6 flex items-center gap-2"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                🎯 <span>Meet Abdul Hakkim A</span>
              </motion.h2>

              <motion.p
                className="text-base md:text-lg leading-relaxed mb-6 text-gray-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                Abdul Hakkim A is a renowned Sales Automation Expert who has helped countless entrepreneurs break free from the manual grind. He runs FREE daily webinars to ensure everyone gets personal attention and can implement immediately.
              </motion.p>

              <motion.ul
                className="space-y-4 text-gray-300 text-base md:text-lg"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {[
                  {
                    emoji: "🎯",
                    color: "text-yellow-400",
                    title: "The Mission:",
                    desc: "To help you stop working 24/7 and start earning while you sleep."
                  },
                  {
                    emoji: "✅",
                    color: "text-green-400",
                    title: "The Strategy:",
                    desc: "Using FREE WhatsApp & AI tools to automate your entire sales process."
                  },
                  {
                    emoji: "📈",
                    color: "text-blue-400",
                    title: "The Result:",
                    desc: "Consistent income, less stress, and a business that grows without your constant presence."
                  }
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    className="flex items-start gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                  >
                    <span className={`${item.color} text-xl mt-1`}>{item.emoji}</span>
                    <div className="flex-1">
                      <span className="font-medium">{item.title}</span>
                      <div className="text-gray-300 mt-1">{item.desc}</div>
                    </div>
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Video Testimonials Section */}
      <motion.section
        className="testimonials-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <motion.div className="section-content">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              🎬 Success Stories - Video Testimonials
            </motion.h2>

            <motion.p
              className="testimonials-subtitle"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              See how others are generating sales on autopilot
            </motion.p>

            <div className="video-grid">
              {testimonialVideos.map((video, index) => (
                <motion.div
                  key={video.id}
                  className="video-card"
                  onClick={() => {
                    setSelectedVideo(video);
                    if (typeof fbq !== 'undefined') {
                      fbq('track', 'ViewContent', {
                        content_name: video.title,
                        content_type: 'video'
                      });
                    }
                  }}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ scale: 1.02, y: -8 }}
                  whileTap={{ scale: 0.98 }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Play testimonial video from ${video.name}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedVideo(video);
                    }
                  }}
                >
                  <div className="video-thumbnail">
                    <img
                      src={video.thumbnail}
                      alt={`${video.title} thumbnail`}
                      loading="lazy"
                      width="300"
                      height="200"
                    />
                    <div className="play-overlay">
                      <FiPlay className="play-icon" />
                    </div>
                  </div>
                  <div className="video-info">
                    <h4>{video.title}</h4>
                    <p>{video.name}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Client Feedback Section */}
      <motion.section
        className="client-feedback-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <motion.div className="section-content">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              What Our Community Members Say
            </motion.h2>

            <motion.p
              className="feedback-subtitle"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Real success stories from entrepreneurs who stopped wasting time and started automating sales
            </motion.p>

            <motion.div
              className="feedback-filter"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <FiFilter className="filter-icon" />
              <select
                value={selectedRoleFilter}
                onChange={(e) => setSelectedRoleFilter(e.target.value)}
                className="role-filter-select"
                aria-label="Filter by role"
              >
                {uniqueRoles.map(role => (
                  <option key={role} value={role}>
                    {role === 'all' ? 'All Roles' : role}
                  </option>
                ))}
              </select>
            </motion.div>

            <div className="feedback-grid">
              {filteredFeedbacks.map((feedback, index) => (
                <motion.div
                  key={feedback.id}
                  className="feedback-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ scale: 1.02, y: -8 }}
                  whileTap={{ scale: 0.98 }}
                  tabIndex={0}
                  role="article"
                  aria-labelledby={`feedback-name-${feedback.id}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleFeedbackExpanded(feedback.id);
                    }
                  }}
                >
                  <div className="feedback-image-container">
                    {feedback.image ? (
                      <img
                        src={feedback.image}
                        alt={`${feedback.name} - ${feedback.role}`}
                        loading="lazy"
                        width="300"
                        height="200"
                        className="feedback-rect-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="avatar-fallback-rect">
                      {getInitials(feedback.name)}
                    </div>
                  </div>

                  <div className="feedback-info">
                    <h3 id={`feedback-name-${feedback.id}`}>{feedback.name}</h3>
                    <p className="feedback-role">{feedback.role}</p>
                  </div>

                  <div className="feedback-quote">
                    <p>
                      {expandedFeedback[feedback.id] || feedback.quote.length <= 140
                        ? feedback.quote
                        : `${feedback.quote.slice(0, 140)}...`}
                    </p>
                    {feedback.quote.length > 140 && (
                      <button
                        className="read-more-btn"
                        onClick={() => toggleFeedbackExpanded(feedback.id)}
                        aria-expanded={!!expandedFeedback[feedback.id]}
                        aria-controls={`feedback-quote-${feedback.id}`}
                      >
                        {expandedFeedback[feedback.id] ? 'Read less' : 'Read more'}
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Bonus Section */}
      <motion.section
        className="bonus-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <motion.div className="section-content">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              🎁 FREE Automation Bonus Vault (Worth ₹25,000)
            </motion.h2>

            <div className="bonus-grid">
              {[
                {
                  img: "https://storage.files-vault.com/uploads/1760966854-BXbDBFXvqB.png",
                  emoji: "🤖",
                  title: "AI Tools Starter Pack",
                  desc: "50+ verified AI tools list with English tutorials (Worth ₹5,000)"
                },
                {
                  img: "https://storage.files-vault.com/uploads/1760966871-XKw3XTHpgg.png",
                  emoji: "📊",
                  title: "Business Automation Templates",
                  desc: "Ready-to-use automation workflows for businesses (Worth ₹8,000)"
                },
                {
                  img: "https://storage.files-vault.com/uploads/1760976790-fakmsXQpOV.png",
                  emoji: "💬",
                  title: "Private Community Access",
                  desc: "Lifetime access to exclusive entrepreneurs group (Worth ₹7,000)"
                },
                {
                  img: "https://storage.files-vault.com/uploads/1760977051-dmxHkhczed.png",
                  emoji: "📞",
                  title: "1-on-1 Strategy Call",
                  desc: "Personal 30-minute session to customize AI for your business (Worth ₹5,000)"
                }
              ].map((bonus, index) => (
                <motion.div
                  key={index}
                  className="bonus-item"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -8, scale: 1.02 }}
                >
                  <img src={bonus.img} alt={bonus.title} className="bonus-image" />
                  <h3>{bonus.emoji} {bonus.title}</h3>
                  <p>{bonus.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* FAQ Section */}
      <motion.section
        className="faq-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <motion.div className="section-content">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Frequently Asked Questions
            </motion.h2>

            <motion.p
              className="faq-subtitle"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Got questions about our FREE Daily Sales Automation Webinar? We've got answers.
            </motion.p>

            <motion.div
              className="faq-search"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search questions..."
                value={faqSearch}
                onChange={(e) => handleFaqSearch(e.target.value)}
                className="search-input"
                aria-label="Search frequently asked questions"
              />
            </motion.div>

            <div className="faq-accordion">
              {filteredFaqs.map((faq, index) => (
                <motion.div
                  key={faq.id}
                  className="faq-item"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  <button
                    className="faq-question"
                    onClick={() => toggleFaq(faq.id)}
                    aria-expanded={expandedFaq === faq.id}
                    aria-controls={`faq-answer-${faq.id}`}
                    id={`faq-question-${faq.id}`}
                  >
                    <span>{faq.question}</span>
                    <FiChevronDown className={`chevron-icon ${expandedFaq === faq.id ? 'expanded' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {expandedFaq === faq.id && (
                      <motion.div
                        id={`faq-answer-${faq.id}`}
                        className="faq-answer"
                        role="region"
                        aria-labelledby={`faq-question-${faq.id}`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="faq-answer-content">
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>

            {filteredFaqs.length === 0 && (
              <motion.div
                className="faq-no-results"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <p>No questions found matching your search. Try different keywords.</p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </motion.section>

      {/* Section 6: Final CTA */}
      <motion.section
        className="final-cta"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <motion.div className="cta-content">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              🔥 Don't Miss Out! Join Today's FREE Sales Automation Webinar!
            </motion.h2>

            <motion.p
              className="cta-subtitle"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Completely FREE • No Hidden Costs • Only 50 Seats Per Session!
            </motion.p>

            {/* Use the new WorkshopTimer component */}
            <WorkshopTimer className="large" />

            <motion.button
              className="cta-primary large free-cta"
              onClick={() => {
                handleManualModalOpen();
                // Track CTA click
                if (typeof fbq !== 'undefined') {
                  fbq('track', 'InitiateCheckout', {
                    content_name: 'Free Sales Automation Webinar - Final CTA - Daily Session',
                    content_category: 'Webinar',
                    value: 0,
                    currency: 'INR'
                  });
                }
              }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              whileHover={{ scale: 1.05, y: -4 }}
              whileTap={{ scale: 0.95 }}
            >
              🎯 Register Now - Completely FREE 🚀
            </motion.button>

            <motion.div
              className="guarantee-text"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <FiCheck className="check-icon" />
              <span>100% FREE • No Credit Card Required • No Hidden Costs</span>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      <AnimatePresence>
        {isFormModalOpen && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setIsFormModalOpen(false);
            }}
          >
            <motion.div
              className="modal-content"
              id="registration-modal"
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              ref={modalRef}
            >
              <div className="modal-header">
                <h3>🎯 Register for FREE Daily Sales Automation Webinar</h3>
                <button
                  className="close-button"
                  onClick={() => setIsFormModalOpen(false)}
                  aria-label="Close registration form"
                >
                  <FiX />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="registration-form">
                <div className="form-group">
                  <label htmlFor="name">
                    <FiUser className="input-icon" />
                    Full Name *
                  </label>
                  <input
                    ref={firstInputRef}
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter your full name"
                    className={formErrors.name ? 'error' : ''}
                    aria-describedby={formErrors.name ? 'name-error' : undefined}
                  />
                  {formErrors.name && (
                    <span id="name-error" className="error-message" role="alert">
                      {formErrors.name}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    <FiMail className="input-icon" />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter your email address"
                    className={formErrors.email ? 'error' : ''}
                    aria-describedby={formErrors.email ? 'email-error' : undefined}
                  />
                  {formErrors.email && (
                    <span id="email-error" className="error-message" role="alert">
                      {formErrors.email}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="phone">
                    <FiPhone className="input-icon" />
                    Phone Number *
                  </label>
                  <div className="phone-input-group">
                    <select
                      value={formData.countryCode}
                      onChange={(e) => handleInputChange('countryCode', e.target.value)}
                      className="country-select"
                      aria-label="Country code"
                    >
                      {countryCodes.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.flag} {country.code}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value.replace(/\D/g, ''))}
                      placeholder="9884899080"
                      className={formErrors.phone ? 'error' : ''}
                      aria-describedby={formErrors.phone ? 'phone-error' : undefined}
                      maxLength="10"
                    />
                  </div>
                  {formErrors.phone && (
                    <span id="phone-error" className="error-message" role="alert">
                      {formErrors.phone}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="profession">
                    <FiBriefcase className="input-icon" />
                    Profession/Role *
                  </label>
                  <select
                    id="profession"
                    name="profession"
                    value={formData.profession}
                    onChange={(e) => handleInputChange('profession', e.target.value)}
                    className={formErrors.profession ? 'error' : ''}
                    aria-describedby={formErrors.profession ? 'profession-error' : undefined}
                  >
                    <option value="">Select your profession</option>
                    <option value="Entrepreneur">Entrepreneur</option>
                    <option value="Business Owner">Business Owner</option>
                    <option value="Startup Founder">Startup Founder</option>
                    <option value="Freelancer">Freelancer</option>
                    <option value="Consultant">Consultant</option>
                    <option value="Executive">Executive</option>
                    <option value="Other">Other</option>
                  </select>
                  {formErrors.profession && (
                    <span id="profession-error" className="error-message" role="alert">
                      {formErrors.profession}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.consent}
                      onChange={(e) => handleInputChange('consent', e.target.checked)}
                      className={formErrors.consent ? 'error' : ''}
                      aria-describedby={formErrors.consent ? 'consent-error' : undefined}
                    />
                    <span className="checkmark"></span>
                    I agree to receive updates about the webinar and understand the terms & conditions *
                  </label>
                  {formErrors.consent && (
                    <span id="consent-error" className="error-message" role="alert">
                      {formErrors.consent}
                    </span>
                  )}
                </div>

                <button
                  type="submit"
                  className="submit-button free-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Processing...' : '🎯 Register Now - Completely FREE 🚀'}
                </button>

                <p className="form-note">
                  After registration, you'll receive the webinar link instantly.
                  <br />
                  <strong>100% FREE • No Credit Card Required</strong>
                </p>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedVideo && (
          <motion.div
            className="video-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setSelectedVideo(null);
            }}
          >
            <motion.div
              className="video-modal-content"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
            >
              <div className="video-modal-header">
                <h3>{selectedVideo.title}</h3>
                <button
                  className="close-button"
                  onClick={() => setSelectedVideo(null)}
                  aria-label="Close video"
                >
                  <FiX />
                </button>
              </div>
              <div className="video-embed-container">
                <iframe
                  src={`https://www.youtube.com/embed/${selectedVideo.embedId}?autoplay=1&rel=0`}
                  title={selectedVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
              <div className="video-fallback">
                <p>
                  Can't view the video?
                  <a href={selectedVideo.url} target="_blank" rel="noopener noreferrer">
                    Open on YouTube
                  </a>
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <p>&copy; 2025 Free Sales Automation Webinar. All rights reserved.</p>
            <p style={{ fontSize: '0.8rem', opacity: '0.7', marginTop: '1rem' }}>
              This site is not part of the Facebook website or Facebook Inc. Additionally, this site is NOT endorsed by Facebook in any way. FACEBOOK is a trademark of FACEBOOK, Inc.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;